import { useCallback, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { EyeIcon } from '@heroicons/react/24/solid'
import { EyeSlashIcon } from '@heroicons/react/24/solid'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useCADPageStore } from '@/state/cad'
import { cn } from '@/utils'

import type { GLTFObject } from '@/lib/cad/GLTFObject'
import type { ASMTreeNode } from '@/state'
import { useOperationSteps } from '@/services/queries/operation_steps'
import { useCADQuery } from '@/services/queries/cads'

export const HideAllPartsButton = ({
  nodes,
  gltf,
}: {
  nodes: ASMTreeNode[]
  gltf: GLTFObject
}) => {
  const { isLoading: isLoadingCadData, data: cadData } = useCADQuery()
  const { isLoading: isLoadingSteps, steps } = useOperationSteps()

  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))
  const allPartsHidden = useCADPageStore((state) => state.allPartsHidden)
  const selectedParts = useCADPageStore((state) => state.selectedParts)
  const hiddenParts = useCADPageStore((state) => state.hiddenParts)
  const operationStep = useCADPageStore(
    useShallow((state) => state.operationStep),
  )
  const explosions = useCADPageStore((state) => state.explosions)
  const isExplosionLinesEnabled = useCADPageStore(
    (state) => state.isExplosionLinesEnabled,
  )
  const showFullModel = useCADPageStore((state) => state.showFullModel)
  const hidePreviousOperations = useCADPageStore(
    (state) => state.hidePreviousOperations,
  )
  const rawAssemblyTree = useMemo(
    () => cadData.rawAssemblyTree,
    [cadData.rawAssemblyTree],
  )

  const hasSelection = selectedParts.length > 0

  // Determine state based on selection
  const { shouldHide, buttonText } = useMemo(() => {
    if (!hasSelection) {
      // No selection - work with all parts
      return {
        shouldHide: !allPartsHidden,
        buttonText: allPartsHidden ? 'Show All' : 'Hide All',
      }
    }

    // Has selection - work with selected parts only
    // If ALL selected parts are hidden, we show them (shouldHide = false)
    // If ANY selected part is visible, we hide them all (shouldHide = true)
    const allSelectedHidden = selectedParts.every((partId) =>
      hiddenParts.includes(partId),
    )

    return {
      shouldHide: !allSelectedHidden,
      buttonText: allSelectedHidden ? 'Show Selected' : 'Hide Selected',
    }
  }, [hasSelection, allPartsHidden, selectedParts, hiddenParts])

  const toggleParts = useCallback(() => {
    let targetNodes: ASMTreeNode[]
    let newHiddenParts: string[]

    if (!hasSelection) {
      // No selection - affect all parts
      targetNodes = nodes
      newHiddenParts = shouldHide ? nodes.map((node) => node.uuid) : []
    } else {
      // Has selection - affect only selected parts
      targetNodes = nodes.filter((node) => selectedParts.includes(node.uuid))

      if (shouldHide) {
        // Add selected parts to hidden parts
        newHiddenParts = [...new Set([...hiddenParts, ...selectedParts])]
      } else {
        // Remove selected parts from hidden parts
        newHiddenParts = hiddenParts.filter(
          (partId) => !selectedParts.includes(partId),
        )
      }
    }

    if (
      !shouldHide &&
      !hasSelection &&
      operationStep &&
      (operationStep.isActive || operationStep.selectFromCad) &&
      rawAssemblyTree
    ) {
      const activeStep = steps.find((s) => s.id === operationStep.stepId)

      if (activeStep) {
        gltf.setVisibilityForOperationSteps({
          assemblyTree: rawAssemblyTree,
          steps,
          hiddenParts: newHiddenParts,
          activeStep,
          explosions,
          isExplosionLinesEnabled,
          showFullModel,
          hidePreviousOperations,
        })

        activeStep.assembly_group_ids.forEach((assemblyGroupId) => {
          const activeNode = nodes.find((n) => n.uuid === assemblyGroupId)
          if (activeNode) {
            gltf.setVisibility(activeNode.instance, !shouldHide)
          }
        })
      }
    } else {
      targetNodes.forEach((node) => {
        gltf.setVisibility(node.instance, !shouldHide)
      })
    }

    // Update state
    setCadPageState({
      hiddenParts: newHiddenParts,
      allPartsHidden: !hasSelection ? shouldHide : allPartsHidden,
    })
  }, [
    nodes,
    hasSelection,
    selectedParts,
    hiddenParts,
    shouldHide,
    allPartsHidden,
    operationStep,
    setCadPageState,
    gltf,
    steps,
    rawAssemblyTree,
    explosions,
    isExplosionLinesEnabled,
    showFullModel,
    hidePreviousOperations,
  ])

  // Show eye icon when we're about to show (currently hidden)
  // Show eye-slash icon when we're about to hide (currently visible)
  const Icon = shouldHide ? EyeSlashIcon : EyeIcon

  if (isLoadingSteps || isLoadingCadData) return null

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            className="h-7 w-7 p-0 rounded hover:bg-gray-100"
            variant="ghost"
            onClick={toggleParts}
          >
            <Icon
              className={cn('w-4 h-4', {
                'text-primary-50': !shouldHide,
                'text-gray-400': shouldHide,
              })}
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent className="bg-white text-black border border-gray-200 shadow-md">
          <p>{buttonText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
